// Sidebar component that displays the list of AI conversation topics.
// Supports cursor-based pagination with IntersectionObserver for infinite scroll.

import { PolymerElement, html } from "@polymer/polymer/polymer-element.js";
import "@polymer/polymer/lib/elements/dom-repeat.js";
import "@polymer/polymer/lib/elements/dom-if.js";
import "@polymer/iron-ajax";
import { afterNextRender } from "@polymer/polymer/lib/utils/render-status.js";

import iconPlusWhite from "../../../../images/icons/icon_plus_white.svg";
import iconEdit from "../../../../images/icons/icon_hamburger_black.svg";
import iconTrash from "../../../../images/icons/icon_trash_red.svg";

import "../ErrorDisplay/ErrorDisplay";
import "./TopicSidebar.style";

class TopicSidebar extends PolymerElement {
  static get template() {
    return html`
      <style include="topic-sidebar-style"></style>

      <iron-ajax
        id="topicsListAjax"
        method="GET"
        url="/api/general/ai/topics"
        handle-as="json"
        on-response="_onTopicsListResponse"
        on-error="_onTopicsListError"
      ></iron-ajax>

      <iron-ajax
        id="deleteTopicAjax"
        method="POST"
        url="/api/general/ai/topic_delete"
        handle-as="json"
        on-response="_onDeleteTopicResponse"
        on-error="_onDeleteTopicError"
      ></iron-ajax>

      <nav class="ai-topic-sidebar" aria-label="Topik percakapan AI">
        <div class="ai-topic-sidebar__new-wrap">
        <button
          type="button"
          class="ai-topic-sidebar__new"
          on-click="_handleClickNewTopic"
        >
          <span class="ai-topic-sidebar__new-text">[[newButtonText]]</span>
          <img src="{{iconPlusWhite}}" alt="" aria-hidden="true" />
        </button>
        </div>

        <template is="dom-if" if="[[isTopicsErrorActive]]">
          <div class="ai-topic-sidebar__error-wrap">
            <error-display
              message="[[topicsErrorMessage]]"
              on-ai-retry="_onTopicsErrorRetry"
            ></error-display>
          </div>
        </template>

        <template is="dom-if" if="[[isDeleteTopicErrorActive]]">
          <div class="ai-topic-sidebar__error-wrap">
            <error-display
              message="[[deleteTopicErrorMessage]]"
              on-ai-retry="_clearDeleteTopicError"
            ></error-display>
          </div>
        </template>

        <ul class="ai-topic-sidebar__list" id="topicListScroll" role="list">
          <template is="dom-repeat" items="[[topicsList]]">
            <li role="listitem" class$="[[_selectedTopicWrap(item.id, selectedTopicId)]]">
              <button
                type="button"
                class="ai-topic-sidebar__row"
                on-click="_handleClickTopicSelected"
                data-topic-id$="[[item.id]]"
              >
                <span class="ai-topic-sidebar__label">[[item.title]]</span>
              </button>
              <button
                type="button"
                class="ai-topic-sidebar__menu-trigger"
                aria-label="Opsi topik"
                aria-haspopup="true"
                aria-expanded$="[[_isMenuOpen(item.id, activeMenuTopicId)]]"
                data-topic-id$="[[item.id]]"
                on-click="_handleClickTopicMenu"
              >
                <img src="[[iconEdit]]" alt="" aria-hidden="true" />
              </button>
              <template is="dom-if" if="[[_isMenuOpen(item.id, activeMenuTopicId)]]">
                <div class="ai-topic-sidebar__menu" role="menu">
                  <button
                    type="button"
                    class="ai-topic-sidebar__menu-item ai-topic-sidebar__menu-item--danger"
                    role="menuitem"
                    data-topic-id$="[[item.id]]"
                    on-click="_handleClickDeleteTopic"
                  >
                    <img class="ai-topic-sidebar__menu-item-icon" src="[[iconTrash]]" alt="" aria-hidden="true" />
                    <span class="ai-topic-sidebar__menu-item-label">Hapus</span>
                  </button>
                </div>
              </template>
            </li>
          </template>
          <template is="dom-if" if="[[hasMoreTopics]]">
            <li id="topicsLoadMoreSentinel" class="ai-topic-sidebar__sentinel" aria-hidden="true"></li>
          </template>
          <template is="dom-if" if="[[topicsLoading]]">
            <li class="ai-topic-sidebar__load-more" aria-hidden="true">
              <span class="ai-topic-sidebar__spinner"></span>
            </li>
          </template>
        </ul>
      </nav>
    `;
  }

  static get properties() {
    return {
      selectedTopicId: {
        type: String,
        value: "",
        reflectToAttribute: true,
      },
      userUid: {
        type: String,
        value: "",
      },
      isLogin: {
        type: Number,
        value: 0,
      },
      newButtonText: {
        type: String,
        value: "Buat Topik Baru",
      },
      topicsList: {
        type: Array,
        value() {
          return [];
        },
      },
      hasMoreTopics: {
        type: Boolean,
        value: false,
      },
      topicsLoading: {
        type: Boolean,
        value: false,
      },
      isTopicsErrorActive: {
        type: Boolean,
        value: false,
      },
      topicsErrorMessage: {
        type: String,
        value: "",
      },
      _topicsAppend: {
        type: Boolean,
        value: false,
      },
      iconPlusWhite: {
        type: String,
        value: iconPlusWhite,
      },
      iconEdit: {
        type: String,
        value: iconEdit,
      },
      iconTrash: {
        type: String,
        value: iconTrash,
      },
      activeMenuTopicId: {
        type: String,
        value: "",
      },
      topicDeleteLoading: {
        type: Boolean,
        value: false,
      },
      _pendingDeleteTopicId: {
        type: String,
        value: "",
      },
      deleteTopicErrorMessage: {
        type: String,
        value: "",
      },
      isDeleteTopicErrorActive: {
        type: Boolean,
        value: false,
      },
    };
  }

  // Initialize the topics list.
  ready() {
    super.ready();
    this._topicsScrollWatcher = null;
    this._onDocumentClick = (event) => {
      if (!this.activeMenuTopicId) return;
      const path = event.composedPath();
      if (!path.includes(this)) this._closeTopicMenu();
    };
    document.addEventListener("click", this._onDocumentClick);
    this._loadTopics(false);
  }

  // Disconnect the topics scroll observer.
  disconnectedCallback() {
    super.disconnectedCallback();
    this._disconnectTopicsScrollObserver();
    if (this._onDocumentClick) {
      document.removeEventListener("click", this._onDocumentClick);
    }
  }

  // Reload the first page of topics from the server.
  refreshTopics() {
    this._loadTopics(false);
  }

  // Fetch topics from server. append=false for first page, append=true for next page.
  _loadTopics(append) {
    if (!this.isLogin || !this.userUid || this.topicsLoading) return;

    this._clearTopicsError();
    this._topicsAppend = append;
    this.topicsLoading = true;

    const params = { user_uid: this.userUid };

    if (append && this.topicsList.length > 0) {
      const lastTopic = this.topicsList[this.topicsList.length - 1];
      if (lastTopic && lastTopic.id) {
        params.last_topic_id = String(lastTopic.id);
      }
    }

    this.$.topicsListAjax.params = params;
    this.$.topicsListAjax.generateRequest();
  }

  // Handle topics list response from server.
  _onTopicsListResponse(event) {
    this.topicsLoading = false;

    const responseBody = event.detail.response;
    if (!responseBody || responseBody.status !== 200) {
      const message = (responseBody && responseBody.message);
      this._showTopicsError(message);
      if (!this._topicsAppend) this.topicsList = [];
      return;
    }

    this._clearTopicsError();

    const data = responseBody.data || {};
    const topics = Array.isArray(data.topics) ? data.topics : [];

    this.topicsList = this._topicsAppend
      ? [...(this.topicsList || []), ...topics]
      : topics;

    this.hasMoreTopics = !!data.has_next_page;

    if (data.new_button_text) {
      this.newButtonText = data.new_button_text;
    }

    // Bind the topics scroll observer.
    this._bindTopicsScrollObserver();
  }

  // Handle topics list error from server.
  _onTopicsListError() {
    this.topicsLoading = false;
    this._showTopicsError();
    if (!this._topicsAppend) this.topicsList = [];
  }

  // Show the topics error message.
  _showTopicsError(message) {
    this.topicsErrorMessage = String(message || "Terjadi kendala. Silakan coba lagi.");
    this.isTopicsErrorActive = true;
  }

  // Clear the topics error message.
  _clearTopicsError() {
    this.isTopicsErrorActive = false;
    this.topicsErrorMessage = "";
  }

  // Retry the topics list.
  _onTopicsErrorRetry() {
    this._clearTopicsError();
    this._loadTopics(false);
  }

  // Disconnect the topics scroll observer.
  _disconnectTopicsScrollObserver() {
    if (!this._topicsScrollWatcher) return;

    try {
      this._topicsScrollWatcher.disconnect();
    } catch (disconnectError) {
      console.error("[_disconnectTopicsScrollObserver] Failed:", disconnectError);
    }
    this._topicsScrollWatcher = null;
  }

  // Watch the bottom sentinel and fetch the next page when the user scrolls near the end.
  _bindTopicsScrollObserver() {
    // Disconnect the topics scroll observer.
    this._disconnectTopicsScrollObserver();
    if (!this.hasMoreTopics) return;

    // Watch the bottom sentinel and fetch the next page when the user scrolls near the end.
    afterNextRender(this, () => {
      if (!this.hasMoreTopics) return;

      const root = this.shadowRoot;
      const sentinel = root && root.querySelector("#topicsLoadMoreSentinel");
      const scrollElement = root && root.querySelector("#topicListScroll");

      if (!sentinel || typeof IntersectionObserver === "undefined") return;

      this._topicsScrollWatcher = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries && entries[0];
          if (!firstEntry || !firstEntry.isIntersecting) return;
          if (!this.hasMoreTopics || this.topicsLoading) return;
          this._loadTopics(true);
        },
        { root: scrollElement || null, threshold: 0 }
      );

      this._topicsScrollWatcher.observe(sentinel);
    });
  }

  _handleClickTopicMenu(event) {
    event.stopPropagation();
    const topicId = event.currentTarget && event.currentTarget.dataset && event.currentTarget.dataset.topicId;
    if (!topicId) return;
    this.activeMenuTopicId =
      String(this.activeMenuTopicId) === String(topicId) ? "" : String(topicId);
  }

  _handleClickDeleteTopic(event) {
    event.stopPropagation();
    const topicId = event.currentTarget && event.currentTarget.dataset && event.currentTarget.dataset.topicId;
    if (!topicId || this.topicDeleteLoading) return;
    if (!this.isLogin || !this.userUid) return;

    this._closeTopicMenu();
    this._clearDeleteTopicError();
    this._pendingDeleteTopicId = String(topicId);
    this.topicDeleteLoading = true;
    this.$.deleteTopicAjax.params = {
      user_uid: this.userUid,
      topic_id: String(topicId),
    };
    this.$.deleteTopicAjax.generateRequest();
  }

  _onDeleteTopicResponse(event) {
    this.topicDeleteLoading = false;

    const responseBody = event.detail.response;
    const topicId = this._pendingDeleteTopicId;
    this._pendingDeleteTopicId = "";

    if (!responseBody || responseBody.status !== 200) {
      const message = (responseBody && responseBody.message);
      this._showDeleteTopicError(message);
      return;
    }

    this._clearDeleteTopicError();
    this.topicsList = (this.topicsList || []).filter(
      (item) => String(item.id) !== String(topicId)
    );

    const isActiveTopic =
      this.selectedTopicId && String(this.selectedTopicId) === String(topicId);
    if (!isActiveTopic) return;

    this.dispatchEvent(
      new CustomEvent("active-topic-deleted", {
        bubbles: true,
        composed: true,
        detail: { topicId: String(topicId) },
      })
    );
  }

  _onDeleteTopicError(event) {
    this.topicDeleteLoading = false;
    this._pendingDeleteTopicId = "";
    this._showDeleteTopicError();
  }

  _showDeleteTopicError(message) {
    this.deleteTopicErrorMessage = String(message || "Terjadi kendala. Silakan coba lagi.");
    this.isDeleteTopicErrorActive = true;
  }

  _clearDeleteTopicError() {
    this.isDeleteTopicErrorActive = false;
    this.deleteTopicErrorMessage = "";
  }

  _closeTopicMenu() {
    this.activeMenuTopicId = "";
  }

  _isMenuOpen(topicId, activeMenuTopicId) {
    return !!(
      topicId &&
      activeMenuTopicId &&
      String(topicId) === String(activeMenuTopicId)
    );
  }

  // Build row wrapper CSS class with selected state highlight.
  _selectedTopicWrap(topicId, selectedId) {
    const base = "ai-topic-sidebar__row-wrap";
    if (topicId && selectedId && String(topicId) === String(selectedId)) {
      return `${base} is-selected`;
    }
    return base;
  }

  // Notify parent (AIPage) to reset chat UI and return to landing state.
  _handleClickNewTopic() {
    this.dispatchEvent(
      new CustomEvent("click-new-topic", {
        bubbles: true,
        composed: true,
      })
    );
  }

  // Notify parent (AIPage) to fetch chat history for a topic.
  _handleClickTopicSelected(event) {
    this._closeTopicMenu();

    const topicId =
      (event.currentTarget && event.currentTarget.dataset && event.currentTarget.dataset.topicId) || "";
    if (!topicId) return;

    this.dispatchEvent(
      new CustomEvent("click-topic-selected", {
        bubbles: true,
        composed: true,
        detail: { topicId: String(topicId) },
      })
    );
  }
}

customElements.define("topic-sidebar", TopicSidebar);
